/**
 * Relationship edit mode effect logic.
 * Extracted from EditorPage to reduce index.tsx size.
 */

import { dia } from '@joint/core'
import { TemplateRelationType } from '../../../../../map-editor-entities/schema/templateSchema'
import { parseCardinality, isTargetLinkedGlobally } from '../../../../../map-editor-features/editor/lib/relationshipUtils'
import type { RelationshipEditState } from '../types'

/**
 * Apply or clear relationship edit mode highlights on target elements.
 * Returns a cleanup function.
 */
export function applyRelationshipEditHighlights(
  graph: dia.Graph | null,
  paper: dia.Paper | null,
  relationshipEditState: RelationshipEditState
): (() => void) | undefined {
  if (!graph || !paper) return undefined

  if (relationshipEditState.editing && relationshipEditState.targetIds.length > 0) {
    // Add custom highlight to available targets with edit mode styling
    relationshipEditState.targetIds.forEach(targetId => {
      const targetElement = graph.getCell(targetId)
      if (targetElement && targetElement.isElement()) {
        const view = paper.findViewByModel(targetElement)
        if (view) {
          // Use a different highlight style for edit mode (green/clickable)
          view.highlight(null, {
            highlighter: {
              name: 'stroke',
              options: {
                padding: 8,
                rx: 8,
                ry: 8,
                attrs: {
                  'stroke-width': 4,
                  stroke: '#10b981', // Green color for available targets
                  'stroke-dasharray': '0', // Solid line (not dashed)
                  filter: 'drop-shadow(0 0 10px rgba(16, 185, 129, 0.5))', // Glow effect
                }
              }
            }
          })

          // Change cursor to pointer for clickable targets
          if (view.el) {
            view.el.style.cursor = 'pointer'
          }
        }
      }
    })
  } else {
    // Clear edit mode highlights and restore cursor
    graph.getElements().forEach(element => {
      const view = paper.findViewByModel(element)
      if (view && view.el) {
        view.el.style.cursor = ''
      }
    })
  }

  // Return cleanup function
  return () => {
    if (!graph || !paper) return
    graph.getElements().forEach(element => {
      const view = paper.findViewByModel(element)
      if (view && view.el) {
        view.el.style.cursor = ''
      }
    })
  }
}

/**
 * Apply a relationship link from the currently edited element to a clicked
 * target. Paper-free core shared by the SVG paper click handler and the Canvas
 * overlay (CANVAS_EDITOR flag) so both paths run identical link logic.
 * Returns true when the link was applied (and edit mode exited).
 */
export function applyRelationLinkToTarget(params: {
  graph: dia.Graph
  relationshipEditState: RelationshipEditState
  selectedElementId: string
  mutableRelationTypes: Record<string, TemplateRelationType>
  handleObjectUpdate: (id: string, updates: Partial<any>) => void
  setRelationshipEditState: React.Dispatch<React.SetStateAction<RelationshipEditState>>
  clickedId: string
}): boolean {
  const {
    graph,
    relationshipEditState,
    selectedElementId,
    mutableRelationTypes,
    handleObjectUpdate,
    setRelationshipEditState,
    clickedId,
  } = params

  // Check if clicked element is an available target
  if (!relationshipEditState.targetIds.includes(clickedId)) return false

  // Get the selected element
  const selectedElement = graph.getCell(selectedElementId)
  if (!selectedElement || !selectedElement.isElement()) return false

  // Get current data
  const currentData = selectedElement.get('data') || {}
  const currentProps = currentData.properties || {}
  const value = currentProps[relationshipEditState.propertyKey!]

  // Get relation config for cardinality check
  const relationConfig = relationshipEditState.relationKey
    ? mutableRelationTypes[relationshipEditState.relationKey]
    : null

  if (!relationConfig) {
    return false
  }

  const maxCount = parseCardinality(relationConfig.cardinality)

  // Check for duplicate global links (if not allowed)
  if (relationConfig.autoLink && !relationConfig.autoLink.allowDuplicates) {
    const { isLinked, linkedBySourceId } = isTargetLinkedGlobally(
      graph,
      relationConfig,
      clickedId,
      selectedElementId,
      undefined
    )

    if (isLinked) {
      const sourceEl = graph.getCell(linkedBySourceId!)
      const sourceName = sourceEl?.get('data')?.properties?.name || linkedBySourceId
      alert(
        `\uC774 \uAC1D\uCCB4\uB294 \uC774\uBBF8 \uB2E4\uB978 \uAC1D\uCCB4(${sourceName})\uC640 \uC5F0\uACB0\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4.\n` +
        `\uAD00\uACC4 \uC124\uC815\uC5D0\uC11C "\uC911\uBCF5 \uC5F0\uACB0 \uD5C8\uC6A9"\uC774 \uBE44\uD65C\uC131\uD654\uB418\uC5B4 \uC788\uC2B5\uB2C8\uB2E4.`
      )
      return false
    }
  }

  let newValue: string | string[]

  if (maxCount === 1) {
    // Single relationship - replace
    newValue = clickedId
  } else {
    // Multiple relationships - add to list
    const list = Array.isArray(value) ? [...value] : (value ? [value] : [])

    // Check if already exists
    if (list.includes(clickedId)) {
      return false
    }

    // Check max count
    if (maxCount !== null && list.length >= maxCount) {
      return false
    }

    list.push(clickedId)
    newValue = list
  }

  // Update the element
  const newData = {
    ...currentData,
    properties: {
      ...currentProps,
      [relationshipEditState.propertyKey!]: newValue
    }
  }

  selectedElement.set('data', newData)
  handleObjectUpdate(selectedElementId, { data: newData })

  // Exit edit mode after successful creation
  setRelationshipEditState({
    editing: false,
    relationKey: null,
    propertyKey: null,
    targetIds: []
  })

  return true
}

/**
 * Create the edit-mode click handler for relationship target selection.
 * Returns the handler function to attach to paper events, or null if not applicable.
 */
export function createEditModeClickHandler(params: {
  graph: dia.Graph
  paper: dia.Paper
  relationshipEditState: RelationshipEditState
  selectedElementId: string
  mutableRelationTypes: Record<string, TemplateRelationType>
  handleObjectUpdate: (id: string, updates: Partial<any>) => void
  setRelationshipEditState: React.Dispatch<React.SetStateAction<RelationshipEditState>>
}): ((elementView: dia.ElementView) => void) {
  const { paper, ...coreParams } = params

  return (elementView: dia.ElementView) => {
    const clickedId = elementView.model.id as string

    const applied = applyRelationLinkToTarget({ ...coreParams, clickedId })

    if (applied) {
        // Show visual feedback with a brief flash animation
        const view = paper.findViewByModel(elementView.model)
        if (view) {
          // Flash the target element
          view.highlight(null, {
            highlighter: {
              name: 'stroke',
              options: {
                padding: 10,
                rx: 10,
                ry: 10,
                attrs: {
                  'stroke-width': 6,
                  stroke: '#10b981',
                  'stroke-dasharray': '0'
                }
              }
            }
          })

          // Remove flash after a short delay
          setTimeout(() => {
            view.unhighlight()
          }, 500)
        }
    }
  }
}
